import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AuthorityMarquee from '../components/AuthorityMarquee';
import SplitCards from '../components/SplitCards';
import ReviewsFeed from '../components/ReviewsFeed';
import { fadeUp, viewport } from '../utils/animations';

const WEBSITE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Bay Area Podcast Studio',
  url: 'https://bayareapodcast.studio',
  description: 'Premier turnkey video podcast studio in Fremont, CA. Serving San Francisco and Silicon Valley executive teams, founders, and creators with 4K multi-cam studio sessions and on-location video production.',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://bayareapodcast.studio/#/studio-sessions',
    },
    'query-input': 'required name=search_term_string',
  },
};

const BREADCRUMB_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://bayareapodcast.studio/' },
    { '@type': 'ListItem', position: 2, name: 'Studio Sessions', item: 'https://bayareapodcast.studio/studio-sessions' },
    { '@type': 'ListItem', position: 3, name: 'Video Services', item: 'https://bayareapodcast.studio/video-services' },
    { '@type': 'ListItem', position: 4, name: 'Our Work', item: 'https://bayareapodcast.studio/our-work' },
    { '@type': 'ListItem', position: 5, name: 'About', item: 'https://bayareapodcast.studio/about' },
  ],
};

const PRECONNECT_ORIGINS = ['https://livid.com', 'https://cdn.livid.com'];

function injectPreconnect(href) {
  if (document.querySelector(`link[rel="preconnect"][href="${href}"]`)) return null;

  const element = document.createElement('link');
  element.rel = 'preconnect';
  element.href = href;
  element.crossOrigin = 'anonymous';
  document.head.appendChild(element);

  return element;
}

export default function Home() {
  const [loadIframe, setLoadIframe] = useState(false);
  const [iframeReady, setIframeReady] = useState(false);

  useEffect(() => {
    document.title = 'Bay Area Podcast Studio | Premium Video Podcast & Content Studio';

    const setMeta = (selector, value) => {
      const element = document.querySelector(selector);
      if (element) element.setAttribute('content', value);
    };

    setMeta('meta[name="description"]', 'Premier turnkey video podcast studio in Fremont, CA. Serving San Francisco and Silicon Valley executive teams, founders, and creators with 4K multi-cam studio sessions and on-location video production.');
    setMeta('meta[property="og:title"]', 'Bay Area Podcast Studio | Premium Video Podcast & Content Studio');
    setMeta('meta[property="og:description"]', 'Turnkey 4K video podcasting and commercial production studio in Fremont, CA.');
    setMeta('meta[property="og:url"]', 'https://bayareapodcast.studio/');
    setMeta('meta[name="twitter:title"]', 'Bay Area Podcast Studio | Premium Video Podcast & Content Studio');
    setMeta('meta[name="twitter:description"]', 'Turnkey 4K video podcasting and commercial production studio in Fremont, CA.');
  }, []);

  useEffect(() => {
    const injectSchema = (id, data) => {
      let element = document.getElementById(id);

      if (!element) {
        element = document.createElement('script');
        element.type = 'application/ld+json';
        element.id = id;
        document.head.appendChild(element);
      }

      element.textContent = JSON.stringify(data);
    };

    injectSchema('ld-home-website', WEBSITE_JSON_LD);
    injectSchema('ld-home-breadcrumb', BREADCRUMB_JSON_LD);

    return () => {
      ['ld-home-website', 'ld-home-breadcrumb'].forEach((id) => {
        document.getElementById(id)?.remove();
      });
    };
  }, []);

  useEffect(() => {
    const injected = PRECONNECT_ORIGINS.map(injectPreconnect).filter(Boolean);
    return () => injected.forEach((element) => element.remove());
  }, []);

  useEffect(() => {
    let idleCallback;
    let timeout;

    const loadHeroVideo = () => {
      if ('requestIdleCallback' in window) {
        idleCallback = window.requestIdleCallback(() => setLoadIframe(true), {
          timeout: 1200,
        });
      } else {
        timeout = window.setTimeout(() => setLoadIframe(true), 400);
      }
    };

    if (document.readyState === 'complete') {
      loadHeroVideo();
    } else {
      window.addEventListener('load', loadHeroVideo, { once: true });
    }

    return () => {
      window.removeEventListener('load', loadHeroVideo);
      if (idleCallback) window.cancelIdleCallback(idleCallback);
      if (timeout) window.clearTimeout(timeout);
    };
  }, []);

  return (
    <main className="pt-[72px]">
      <section className="relative w-full h-[calc(100vh-72px)] min-h-[600px] overflow-hidden bg-[#000000]">
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden bg-black">
          {loadIframe && (
            <iframe
              className={`absolute top-1/2 left-1/2 w-[100vw] h-[56.25vw] min-w-[177.77vh] min-h-[100vh] -translate-x-1/2 -translate-y-1/2 transition-opacity duration-1000 ease-in ${iframeReady ? 'opacity-100' : 'opacity-0'}`}
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture; web-share"
              allowFullScreen
              frameBorder="0"
              referrerPolicy="strict-origin-when-cross-origin"
              src="https://livid.com/embed/63SZ452qaOKc?autoplay=1&loop=1&muted=1"
              title="Bay Area Podcast Studio demo reel"
              loading="eager"
              onLoad={() => setIframeReady(true)}
            />
          )}
        </div>

        <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />

        <motion.div
          className="absolute inset-0 z-20 flex flex-col justify-end items-center text-center pb-10 md:pb-16 px-5 md:px-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1 className="text-white font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl mb-4 drop-shadow-md">
            The Bay Area&apos;s Premium Video Podcast &amp; Content Studio.
          </h1>
          <p className="text-white font-medium text-sm sm:text-base md:text-xl max-w-xl md:max-w-2xl mb-8 opacity-90 leading-relaxed drop-shadow-md">
            The space to record your show. The team to handle your video production.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center w-full max-w-sm sm:max-w-none">
            <Link
              to="/studio-sessions"
              className="bg-white text-black text-xs md:text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 w-full sm:w-auto text-center rounded-[2px] transition-all duration-150 ease-in-out active:scale-95"
            >
              BOOK THE STUDIO
            </Link>
            <Link
              to="/video-services"
              className="bg-transparent border border-white text-white text-xs md:text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 w-full sm:w-auto text-center rounded-[2px] transition-all duration-150 ease-in-out hover:bg-white hover:text-black"
            >
              VIEW VIDEO SERVICES
            </Link>
          </div>
        </motion.div>
      </section>

      <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
        <AuthorityMarquee />
      </motion.div>

      <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
        <SplitCards />
      </motion.div>

      <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
        <ReviewsFeed />
      </motion.div>

      <motion.section
        className="bg-white text-black py-16 md:py-24 px-6 border-t-2 border-black"
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        variants={fadeUp}
      >
        <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-8">
          <Link
            to="/portfolio"
            className="group border-2 border-black p-6 md:p-8 text-center transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl"
          >
            <p className="text-black text-xl md:text-2xl font-black uppercase tracking-tight mb-5">
              See the work
            </p>
            <span className="block bg-white text-black border-2 border-black group-hover:bg-black group-hover:text-white text-xs md:text-sm font-bold uppercase tracking-[0.15em] px-6 py-4 transition-colors">
              View Portfolio
            </span>
          </Link>

          <Link
            to="/studio-sessions"
            className="group border-2 border-black p-6 md:p-8 text-center transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl"
          >
            <p className="text-black text-xl md:text-2xl font-black uppercase tracking-tight mb-5">
              Lock in date
            </p>
            <span className="block bg-black text-white border-2 border-black group-hover:bg-white group-hover:text-black text-xs md:text-sm font-bold uppercase tracking-[0.15em] px-6 py-4 transition-colors">
              Book Studio
            </span>
          </Link>
        </div>
      </motion.section>
    </main>
  );
}